/* eslint-disable no-nested-ternary */
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { DownloadOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { useEffect, useMemo, useState } from 'react';
import { isDesktop } from 'renderer/shared/constants/environment';
import {
  useAssignLicenseMutation,
  useListProductsQuery,
} from 'renderer/features/products-page/services/products.endpoints';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { selectUser } from 'renderer/features/auth/state/userSelectors';
import { getLicenseDownloadUrlFromCatalog } from '../utils/getLicenseDownloadUrl';
import { License, Licenses } from '../schemas/licenses.schemas';
import {
  getLicensePurchasedDisplay,
  getLicenseStatusChip,
  isTrialLicense,
} from '../utils/licenseStatus';

interface LicensesTableProps {
  licenses: Licenses;
  email: string;
}

const tableStyles = {
  tableContainer: {
    // marginTop: '20px',
    marginBottom: '20px',
    border: '1px solid rgba(224, 224, 224, 1)',
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
  },
  tableCell: {
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  },
  tableRow: {
    '&:last-child td': {
      border: 0,
    },
  },
};

const getLicenseProductLabel = (product: License['product']) => {
  const name =
    product.displayName || product.description || product.name || 'Unknown';
  const withVersion = product.version ? `${name} ${product.version}` : name;
  return product.host ? `${withVersion} (${product.host})` : withVersion;
};

const LicensesTable = ({ licenses, email }: LicensesTableProps) => {
  const theme = useTheme();
  const user = useAppSelector(selectUser);
  const isAdmin = user?.role === 'admin';
  const { data: products } = useListProductsQuery({
    includeHidden: isAdmin,
  });
  const [openModal, setOpenModal] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [currentSerial, setCurrentSerial] = useState<string | null>(null);

  const [
    assignLicense,
    {
      isLoading: assignLoading,
      isSuccess: assignSuccess,
      error: assignError,
      reset: resetAssign,
    },
  ] = useAssignLicenseMutation();

  useEffect(() => {
    if (assignSuccess) {
      resetAssign();
      setRemoveModalOpen(false);
    }
  }, [assignSuccess, resetAssign]);

  const handleOpenModal = (serial: any) => {
    setCurrentSerial(serial);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const licenseToRemove = useMemo(
    () => licenses.find((license) => license.serial === currentSerial),
    [licenses, currentSerial],
  );
  const isRemoveTargetTrial = licenseToRemove
    ? isTrialLicense(licenseToRemove)
    : false;

  return (
    <>
      <TableContainer component={Paper} sx={tableStyles.tableContainer}>
        {/* <Table> */}
        <Table sx={tableStyles.table} aria-label="licenses table" size="small">
          <TableHead sx={tableStyles.tableHead}>
            <TableRow>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                PRODUCT
              </TableCell>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                SERIAL NUMBER
              </TableCell>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                ACTIVATIONS
              </TableCell>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                STATUS
              </TableCell>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                PURCHASED
              </TableCell>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                ACTIONS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {licenses.map((license: License) => {
              const { serial, activations, count, product } = license;
              const statusChip = getLicenseStatusChip(license);
              const downloadUrl = getLicenseDownloadUrlFromCatalog(
                product,
                products,
              );

              return (
                <TableRow key={serial} sx={tableStyles.tableRow}>
                  <TableCell sx={tableStyles.tableCell}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {getLicenseProductLabel(product)}
                      {downloadUrl && (
                        <Tooltip title="Download" placement="top">
                          <IconButton
                            component="a"
                            href={downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download"
                            size="small"
                          >
                            <DownloadOutlined
                              style={{ color: theme.palette.grey[900] }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={tableStyles.tableCell}>
                    {serial}
                    {isDesktop && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenModal(serial)}
                        sx={{ ml: 1 }}
                      >
                        Manage
                      </Button>
                    )}
                  </TableCell>
                  <TableCell sx={tableStyles.tableCell}>
                    {activations.length} / {count}
                  </TableCell>
                  <TableCell sx={tableStyles.tableCell}>
                    <Chip
                      sx={{
                        color: 'common.white',
                        ml: 1,
                        padding: 0.5,
                        borderRadius: 1,
                        fontWeight: 'bold',
                        height: 'auto',
                        width: 150,
                        textAlign: 'center',
                        ...(statusChip.useCustomWarningStyle && {
                          backgroundColor: 'warning.light',
                        }),
                        '& .MuiChip-label': {
                          display: 'block',
                          whiteSpace: 'pre-line',
                        },
                      }}
                      color={statusChip.color}
                      label={statusChip.label}
                    />
                  </TableCell>
                  <TableCell sx={tableStyles.tableCell}>
                    {getLicensePurchasedDisplay(license)}
                  </TableCell>
                  <TableCell sx={tableStyles.tableCell}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => {
                        setCurrentSerial(serial);
                        setRemoveModalOpen(true);
                      }}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={removeModalOpen} onClose={() => setRemoveModalOpen(false)}>
        <DialogTitle>Remove License From Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove license {currentSerial} from this
            account?
          </DialogContentText>
          {isRemoveTargetTrial && (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              Trial license cannot be deleted
            </Typography>
          )}
          {assignError && (
            <Alert severity="error">{formatQueryError(assignError)}</Alert>
          )}
          {assignSuccess && (
            <Alert severity="success">Successfully Removed!</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setRemoveModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={assignLoading || assignSuccess || isRemoveTargetTrial}
            onClick={() =>
              assignLicense({
                serial: currentSerial ?? '',
                email,
                removeUser: true,
              })
            }
          >
            Yes, Remove
          </Button>
        </DialogActions>
      </Dialog>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="manage-serial-title"
        aria-describedby="manage-serial-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            // width: 300,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="manage-serial-title" variant="h6" component="h2">
            Serial: {currentSerial}
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Activate Serial" />
              <Button variant="contained" size="small" onClick={() => {}}>
                Activate
              </Button>
            </ListItem>
            <ListItem>
              <ListItemText primary="Deactivate Serial" />
              <Button variant="contained" size="small" onClick={() => {}}>
                Deactivate
              </Button>
            </ListItem>
          </List>
          <Button variant="outlined" onClick={handleCloseModal} sx={{ mt: 2 }}>
            Close
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default LicensesTable;
